import { useCallback } from "react";

import { IconPlus, IconRefresh } from "@tabler/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { useGetPricingsQuery } from "@sso/apis/pricing/hooks";
import { TableHeader } from "@sso/components/TableHeader";
import { TableLayoutWrapper } from "@sso/components/TableLayoutWrapper";
import { TablePagination } from "@sso/components/TablePagination";
import { Dialog } from "@sso/constant";
import { TableProvider, TableProviderState } from "@sso/context/tableContext";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { openDialog } from "@sso/store/slices/dialog";
import { selectPricingPagination } from "@sso/store/slices/pricing";
import { Button } from "@sso/ui/Button";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";

import PricingsTable from "./pricingsTable";
import { pricingTableColumns } from "./pricingsTableColumns";

const Pricing = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const pagination = useAppSelector(selectPricingPagination);
  const navigate = useNavigate();
  const { data, isFetching, error, refetch } = useGetPricingsQuery();

  const tableContextInstance: TableProviderState = { ...pagination, columns: pricingTableColumns };

  const handleAddPricing = useCallback(() => {
    dispatch(openDialog({ type: Dialog.ADD_PRICING }));
  }, [dispatch]);

  const { softwareName, licenceType } = useParams();

  const handleBackToProducts = () => {
    navigate(`/management/licence-types/${softwareName}`);
  };

  const TableActions = (
    <Flex>
      <IconButton title="Refresh" onClick={refetch}>
        <IconRefresh />
      </IconButton>
      <IconButton title="Add pricing" onClick={handleAddPricing}>
        <IconPlus />
      </IconButton>
    </Flex>
  );

  const FooterActions = (
    <Button onClick={handleBackToProducts} fontSize="1rem">
      Back to licence types
    </Button>
  );

  return (
    <>
      {isFetching && <CircleLoading color={theme.colors.secondary.dark} size={8} blurBackground />}
      <TableProvider tableInstance={tableContextInstance}>
        <TableHeader hasSearch={false} title={`${licenceType} Pricing`} TableActions={TableActions} />
        <TableLayoutWrapper error={error} data={data}>
          {data && <PricingsTable data={data.resources} tableColumns={pricingTableColumns} />}
        </TableLayoutWrapper>
        <TablePagination actionButtons={FooterActions} totalPages={data?.totalPages} />
      </TableProvider>
    </>
  );
};

export default Pricing;
